import { Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import { useBranch } from "../../../../context/BranchContext";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import InputMoney from "../../../components/Forms/Element/InputMoney";
import InputDate from "../../../components/Forms/Element/InputDate";
import TextArea from "../../../components/Forms/Element/TextArea";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import FormSection from "../../../components/Common/Form/FormSection";
import { parseYYYMMDDToDate } from "../../../../utils/DateUtils";

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, entityData, resetForm } = props;
    const { selectedBranch } = useBranch();

    // Filial travada quando o operador já escolheu uma no topo da tela. Com "todas"
    // selecionado o campo abre, porque aí o vale precisa dizer a qual filial pertence.
    const [disabledBranch, setDisabledBranch] = useState(false);

    useEffect(() => {
        if (selectedBranch === null) {
            setDisabledBranch(false);
            formik.setFieldValue('branch', null);
        } else {
            formik.setFieldValue('branch', {
                label: selectedBranch.name,
                value: selectedBranch.id
            });
            setDisabledBranch(true);
        }
    }, [selectedBranch, resetForm]);

    useEffect(() => {
        // Preenche os campos na edição de um vale já lançado.
        if (entityData) {
            formik.setFieldValue('value', entityData.value);
            formik.setFieldValue('advanceDate', parseYYYMMDDToDate(entityData.advanceDate));
            formik.setFieldValue('description', entityData.description || '');

            if (entityData.employee) {
                formik.setFieldValue('employee', {
                    label: entityData.employee.fullName,
                    value: entityData.employee.id
                });
            }

            if (entityData.branch) {
                formik.setFieldValue('branch', {
                    label: entityData.branch.name,
                    value: entityData.branch.id
                });
            }
        }
    }, [entityData]);

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('cash_flow.advance')}
                    description={t('cash_flow.advance_information_hint')}
                    icon="fa-solid fa-hand-holding-dollar"
                >
                    {/* A filial abre o formulário porque ela filtra o colaborador: escolher
                        a pessoa antes da unidade levaria a listar quem não trabalha nela.
                        g-3 com colunas md para os campos empilharem no celular em vez de
                        espremer os selects. */}
                    <Row className="g-3">
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('branch')} <AsteriskRequired/></FormLabel>
                                <BranchSelect
                                    name="branch"
                                    isDisabled={disabledBranch}
                                    value={formik.values.branch}
                                    onChange={(value) => {
                                        formik.setFieldValue('branch', value);
                                        // Trocar a filial invalida quem estava escolhido: ele
                                        // pode não atender na unidade nova, e o vale iria para
                                        // a filial errada sem ninguém perceber.
                                        formik.setFieldValue('employee', null);
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.branch}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('employee')} <AsteriskRequired/></FormLabel>
                                {/* filterProvidesService desligado: recepção e gerente também
                                    pegam vale, e o padrão do componente só traz quem atende.
                                    A key força o select a recarregar quando a filial muda: sem
                                    isso ele mantinha em cache a lista da filial anterior. */}
                                <EmployeeSelect
                                    key={formik.values.branch?.value ?? 'all'}
                                    branch={formik.values.branch}
                                    filterProvidesService={false}
                                    value={formik.values.employee}
                                    onChange={(value) => formik.setFieldValue('employee', value)}
                                />
                                {formik.errors.employee && (
                                    <div className="text-danger fs-12 mt-1">{formik.errors.employee}</div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className="g-3 mt-0">
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('value')} (R$) <AsteriskRequired/></FormLabel>
                                <InputMoney
                                    name="value"
                                    value={formik.values.value}
                                    onChange={(value) => formik.setFieldValue('value', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.value}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('date')} <AsteriskRequired/></FormLabel>
                                <InputDate
                                    name="advanceDate"
                                    selected={formik.values.advanceDate}
                                    onChange={(value) => formik.setFieldValue('advanceDate', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.advanceDate}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Observação sozinha na linha: é o campo mais longo do formulário e,
                        dividindo espaço com a filial, sobrava uma caixa estreita para um
                        texto que costuma ser uma frase inteira. */}
                    <Row className="g-3 mt-0">
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('description')}</FormLabel>
                                <TextArea
                                    name="description"
                                    placeholder={t('cash_flow.advance_description_placeholder')}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    maxLength={255}
                                    style={{ height: "80px" }}
                                    counter="true"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
